import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Upload, Copy, Trash2, Check, Search, Info } from 'lucide-react';
import { listMedia, uploadMedia, deleteMedia, type MediaItem } from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { formatDateTime } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';

function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function MediaList() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = user?.role === 'admin' || user?.role === 'redakteur';

  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['media'], queryFn: () => listMedia() });

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => Promise.all(files.map(f => uploadMedia(f, 'general'))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['media'] }),
    onError: (e) => alert('Upload fehlgeschlagen: ' + (e as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (item: MediaItem) => deleteMedia(item),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['media'] }),
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    try { await uploadMutation.mutateAsync(files); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCopy = async (item: MediaItem) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      window.prompt('URL kopieren:', item.url);
    }
  };

  const handleDelete = (item: MediaItem) => {
    if (!confirm(`Bild "${item.filename}" wirklich löschen?\n\nAchtung: Bereits versendete Kampagnen behalten das Bild über die gecachte URL. Bei neuen Kampagnen ist es dann nicht mehr verfügbar.`)) return;
    deleteMutation.mutate(item);
  };

  const filtered = (data ?? []).filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return m.filename.toLowerCase().includes(q) || (m.alt_text?.toLowerCase().includes(q) ?? false);
  });

  return (
    <div>
      <PageHeader
        title="Mediathek"
        subtitle="Alle hochgeladenen Bilder. Im Kampagnen-Editor hochgeladene Bilder landen automatisch hier."
        actions={canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload size={16} /> {uploading ? 'Lade hoch...' : 'Bilder hochladen'}
            </button>
          </>
        )}
      />

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} />
        <div>
          <strong>Mediathek:</strong> Alle Bilder, die du im Kampagnen- oder Vorlagen-Editor verwendest, landen hier.
          Du kannst auch direkt hier hochladen und die URL per Klick auf das Kopier-Icon übernehmen.
          Empfohlen: maximal 1 MB pro Bild, Format JPG oder PNG. Tipp: Bilder vorher auf
          <a href="https://squoosh.app" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}> squoosh.app</a> komprimieren.
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div className="search-input-wrapper" style={{ maxWidth: '420px' }}>
          <Search size={16} />
          <input
            className="search-input"
            placeholder="Dateiname oder Alt-Text suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title={search ? 'Keine Treffer' : 'Noch keine Bilder'}
          description={search
            ? 'Versuche eine andere Suche.'
            : 'Lädst du Bilder im Kampagnen-Editor hoch, landen sie automatisch hier. Oder nutze den Button oben rechts.'}
          action={canEdit && !search && (
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
              <Upload size={16} /> Erste Bilder hochladen
            </button>
          )}
        />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem',
        }}>
          {filtered.map(m => (
            <div
              key={m.id}
              style={{
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: '100%', aspectRatio: '4 / 3',
                background: 'var(--surface-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                <img src={m.url} alt={m.alt_text ?? m.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '0.75rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.filename}>
                  {m.filename}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', display: 'flex', gap: '0.5rem' }}>
                  {m.width && m.height && <span>{m.width}×{m.height}</span>}
                  {m.size_bytes && <span>{fmtSize(m.size_bytes)}</span>}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>{formatDateTime(m.created_at)}</div>
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.35rem' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flex: 1, fontSize: '0.75rem' }}
                    onClick={() => handleCopy(m)}
                    title="URL kopieren"
                  >
                    {copiedId === m.id ? <><Check size={12} /> Kopiert</> : <><Copy size={12} /> URL</>}
                  </button>
                  {canEdit && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => handleDelete(m)}
                      title="Löschen"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="alert alert-info" style={{ marginTop: '2rem' }}>
        <ImageIcon size={16} />
        <div>
          <strong>Tipp:</strong> Im Drag-&amp;-Drop-Editor kannst du einfach ein Bild-Block einfügen und ein neues Bild hochladen — das landet automatisch hier. Zum Wiederverwenden eines vorhandenen Bildes: auf <strong>URL</strong> klicken, dann im Editor beim Bild-Block „URL einfügen“ wählen und einfügen.
        </div>
      </div>
    </div>
  );
}
